import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../services/api';

const Footer = ({ isSplash = false }) => {
  const [displayStats, setDisplayStats] = useState({
    totalUsers: 0,
    totalContent: 0,
    totalInstructors: 0,
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    humanAnswer: '',
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/statistics/public');
        const newStats = {
          totalUsers: response.data.total_users || 0,
          totalContent: response.data.total_content || 0,
          totalInstructors: response.data.total_instructors || 0,
        };
        // Animate counting - slower so users can see it
        
        const duration = 3000; // 3 seconds
        const steps = 100; // 100 steps for smoother animation
        const stepDuration = duration / steps;
        
        const animateValue = (start, end, callback) => {
          const increment = (end - start) / steps;
          let current = start;
          let step = 0;
          
          const timer = setInterval(() => {
            step++;
            current = start + (increment * step);
            if (step >= steps) {
              current = end;
              callback(Math.round(current));
              clearInterval(timer);
            } else {
              callback(Math.round(current));
            }
          }, stepDuration);
        };
        
        animateValue(0, newStats.totalUsers, (val) => {
          setDisplayStats(prev => ({ ...prev, totalUsers: val }));
        });
        
        setTimeout(() => {
          animateValue(0, newStats.totalContent, (val) => {
            setDisplayStats(prev => ({ ...prev, totalContent: val }));
          });
        }, 100);
        
        setTimeout(() => {
          animateValue(0, newStats.totalInstructors, (val) => {
            setDisplayStats(prev => ({ ...prev, totalInstructors: val }));
          });
        }, 200);
        
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    setContactSuccess(false);

    // Simple human validation (anti-bot)
    if (contactForm.humanAnswer.trim().toLowerCase() !== 'med') {
      setContactLoading(false);
      setContactError("Verification failed. Please type the code shown.");
      return;
    }

    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // await api.post('/contact', contactForm);
      
      // Simulate API call for now
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setContactSuccess(true);
      setContactForm({ name: '', email: '', message: '', humanAnswer: '' });
      setTimeout(() => setContactSuccess(false), 5000);
    } catch (error) {
      setContactError(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const textColor = isSplash ? 'text-white' : 'text-gray-300';
  const textSecondary = isSplash ? 'text-white/80' : 'text-gray-400';
  const textMuted = isSplash ? 'text-white/60' : 'text-gray-500';
  const headingColor = isSplash ? 'text-white' : 'text-white';
  const linkHover = isSplash ? 'hover:text-white' : 'hover:text-white';
  
  return (
    <footer className={`${isSplash ? 'bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 border-t border-purple-500/30' : 'bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900'} ${textColor} mt-auto`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <h3 className={`text-2xl font-bold ${headingColor} mb-4`}>ALIEN GREEN (EduTechnologies)</h3>
            <p className={`${textSecondary} mb-4`}>
              Your trusted platform for medical education and exam preparation. 
              Access quality content, practice questions, and track your progress. 
              Perfect for licensing candidates and university students preparing for end of semester examinations.
            </p>
            <div className="flex space-x-4 mb-6">
              <button type="button" className={`${textSecondary} ${linkHover} transition-colors`} title="Facebook (coming soon)" aria-label="Facebook">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
              <button type="button" className={`${textSecondary} ${linkHover} transition-colors`} title="X / Twitter (coming soon)" aria-label="X (Twitter)">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </button>
              <button type="button" className={`${textSecondary} ${linkHover} transition-colors`} title="Instagram (coming soon)" aria-label="Instagram">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.246 1.805-.413 2.227-.217.562-.477.96-.896 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.413-.569-.224-.96-.479-1.379-.897-.419-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                </svg>
              </button>
            </div>
            
            {/* Statistics */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${headingColor} mb-1`}>
                  {displayStats.totalUsers.toLocaleString()}
                </div>
                <div className={`text-sm ${textMuted}`}>Users</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${headingColor} mb-1`}>
                  {displayStats.totalContent.toLocaleString()}
                </div>
                <div className={`text-sm ${textMuted}`}>Content</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${headingColor} mb-1`}>
                  {displayStats.totalInstructors.toLocaleString()}
                </div>
                <div className={`text-sm ${textMuted}`}>Instructors</div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`${headingColor} font-semibold mb-4`}>Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className={`${textSecondary} ${linkHover} transition-colors`}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className={`${textSecondary} ${linkHover} transition-colors`}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className={`${textSecondary} ${linkHover} transition-colors`}>
                  Services
                </Link>
              </li>
              <li>
                <Link to="/faq" className={`${textSecondary} ${linkHover} transition-colors`}>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/login" className={`${textSecondary} ${linkHover} transition-colors`}>
                  Login
                </Link>
              </li>
              <li>
                <Link to="/register" className={`${textSecondary} ${linkHover} transition-colors`}>
                  Register
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Us Form */}
          <div>
            <h4 className={`${headingColor} font-semibold mb-4`}>Contact Us</h4>
            <form onSubmit={handleContactSubmit} className="space-y-3">
              {contactSuccess && (
                <div className="bg-green-500/20 border border-green-500/50 text-green-300 text-sm p-2 rounded">
                  Message sent successfully!
                </div>
              )}
              {contactError && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-300 text-sm p-2 rounded">
                  {contactError}
                </div>
              )}
              <div>
                <input
                  type="text"
                  placeholder="Charles Makoye"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 ${textColor} placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent`}
                  disabled={contactLoading}
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Your Email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 ${textColor} placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent`}
                  disabled={contactLoading}
                />
              </div>
              <div>
                <textarea
                  placeholder="Your Message"
                  required
                  rows={3}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 ${textColor} placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent resize-none`}
                  disabled={contactLoading}
                />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1">
                  <span className="sr-only">Anti-bot check</span>
                  <span className="inline-flex items-center gap-2">
                    <span>Type</span>
                    <span
                      aria-hidden="true"
                      className="inline-flex items-center rounded-md bg-white/10 border border-white/20 px-2 py-1 text-[11px] font-extrabold tracking-[0.35em] text-white/95"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
                      onCopy={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      M&nbsp;E&nbsp;D
                    </span>
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Type code here"
                  required
                  value={contactForm.humanAnswer}
                  onChange={(e) => setContactForm({ ...contactForm, humanAnswer: e.target.value })}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  autoComplete="off"
                  spellCheck={false}
                  className={`w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 ${textColor} placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent`}
                  disabled={contactLoading}
                />
              </div>
              <button
                type="submit"
                disabled={contactLoading}
                className={`w-full px-4 py-2 rounded-lg bg-white text-purple-600 font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {contactLoading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Support */}
          <div>
            <h4 className={`${headingColor} font-semibold mb-4`}>Support</h4>
            <ul className="space-y-3">
              <li>
                <div className="flex items-start gap-2">
                  <svg className={`w-5 h-5 ${isSplash ? 'text-white/80' : 'text-primary-400'} mt-0.5 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className={`${textMuted} text-sm`}>Email</p>
                    <a href="mailto:support@pharmagreen.co.tz" className={`${headingColor} ${isSplash ? 'hover:text-white' : 'hover:text-primary-400'} transition-colors`}>
                      support@pharmagreen.co.tz
                    </a>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-2">
                  <svg className={`w-5 h-5 ${isSplash ? 'text-white/80' : 'text-primary-400'} mt-0.5 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <p className={`${textMuted} text-sm`}>Phone</p>
                    <a href="tel:+255711416873" className={`${headingColor} ${isSplash ? 'hover:text-white' : 'hover:text-primary-400'} transition-colors`}>
                      +255 711 416 873
                    </a>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-2">
                  <svg className={`w-5 h-5 ${isSplash ? 'text-white/80' : 'text-primary-400'} mt-0.5 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className={`${textMuted} text-sm`}>Location</p>
                    <p className={headingColor}>Mbweni, Dar es Salaam</p>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-2">
                  <svg className={`w-5 h-5 ${isSplash ? 'text-white/80' : 'text-primary-400'} mt-0.5 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className={`${textMuted} text-sm`}>Hours</p>
                    <p className={headingColor}>Mon - Fri: 8AM - 6PM</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-10 pt-6 border-t ${isSplash ? 'border-white/10' : 'border-white/10'}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className={`${textMuted} text-sm text-center md:text-left`}>
              © {new Date().getFullYear()} <span className="font-semibold text-white/90">ALIEN GREEN (EduTechnologies)</span>. All rights reserved.
            </p>
            <div className="flex items-center justify-center md:justify-end gap-5 text-sm">
              <Link
                to="/privacy-policy"
                className={`${textMuted} hover:text-white transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/50`}
              >
                Privacy Policy
              </Link>
              <span className="text-white/20">•</span>
              <Link
                to="/terms-of-service"
                className={`${textMuted} hover:text-white transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/50`}
              >
                Terms of Service
              </Link>
            </div>
          </div>
          <p className={`${textMuted} text-xs mt-3 text-center md:text-left`}>
            Past papers, revision papers, and practice materials are provided strictly for learning and exam revision purposes only.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
